import { useCallback, useRef, useState } from 'react';

import { normalizeTasksPeriod } from '../lib/normalizeTasksPeriod';

import { useTasksPeriodPickerEffects } from './useTasksPeriodPickerEffects';

import type { TasksPeriod } from '../types';

interface UseTasksPeriodPickerParams {
  value?: TasksPeriod | null;
  onSave(period: TasksPeriod | undefined): void;
}

// Календарь периода реестра задач закрывается кнопкой «Сохранить» (применяет
// период и обновляет реестр) или кликом вне календаря (как раньше: выбранные
// даты остаются в поле до «Поиска») — us/11225.
export const useTasksPeriodPicker = ({
  value,
  onSave,
}: UseTasksPeriodPickerParams) => {
  const [isOpen, setIsOpen] = useState(false);
  // Выбранное в календаре, включая одну дату без второй.
  const [draftPeriod, setDraftPeriod] = useState<TasksPeriod | null>(null);
  // RangePicker (rc-picker) сам закрывает календарь сразу после второй даты —
  // в том же событии, что и onCalendarChange. Это закрытие пропускаем, чтобы
  // до «Сохранить» можно было дотянуться.
  const closeGuardRef = useRef(false);

  useTasksPeriodPickerEffects({ draftPeriod, closeGuardRef });

  const openChangeHandler = useCallback(
    (open: boolean) => {
      if (open) {
        setDraftPeriod(value ?? null);
        setIsOpen(true);
        return;
      }

      if (closeGuardRef.current) {
        closeGuardRef.current = false;
        return;
      }

      setIsOpen(false);
    },
    [value]
  );

  const calendarChangeHandler = useCallback((period: TasksPeriod) => {
    const [start, end] = period;

    setDraftPeriod(period);
    closeGuardRef.current = Boolean(start && end);
  }, []);

  const saveHandler = useCallback(() => {
    closeGuardRef.current = false;
    setIsOpen(false);
    onSave(normalizeTasksPeriod(draftPeriod));
  }, [draftPeriod, onSave]);

  return {
    isOpen,
    openChangeHandler,
    calendarChangeHandler,
    saveHandler,
  };
};
